/**
 * The definitions screen (REQ-021 AC-001).
 *
 * Every metric, its formula, its source, its owner and the date its definition
 * last changed. None of that is written here: the page renders
 * `/api/staff/dashboard/definitions`, which is generated from the same records
 * the computation reads. There is therefore no copy of a formula to drift out
 * of step with the code — a metric that quietly changes meaning is worse than
 * one that is missing, and the only defence against that is having one record.
 *
 * The MAR-001 §9 targets are on the same screen, with the author and the date
 * each was set, because a target is a definition too (AC-006).
 */
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, BookOpen, Loader2 } from "lucide-react";
import apiClient from "@/services/api/apiClient";
import {
  groupLabel,
  refreshLabel,
  UNMEASURED_SOURCE,
  type DefinitionsPayload,
} from "./metrics";

export default function DashboardDefinitionsPage() {
  const definitions = useQuery({
    queryKey: ["dashboard", "definitions"],
    queryFn: async () => {
      const response = await apiClient.get<{ data: DefinitionsPayload }>(
        "/staff/dashboard/definitions",
      );
      return response.data.data;
    },
    retry: false,
  });

  const payload = definitions.data;

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
          <BookOpen className="h-5 w-5 text-sky-600" /> Metric definitions
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          Generated from the same records the dashboard computes from, so a
          definition here cannot disagree with the number it describes.
        </p>
      </div>

      {definitions.isLoading && (
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {definitions.isError && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>The definitions could not be read.</p>
        </div>
      )}

      {payload &&
        payload.groups.map((group) => {
          const metrics = payload.metrics.filter(
            (metric) => metric.group === group,
          );
          if (metrics.length === 0) return null;

          return (
            <section
              key={group}
              data-testid={`definitions-${group}`}
              className="overflow-hidden rounded-lg border border-gray-200 bg-white"
            >
              <h2 className="border-b border-gray-200 px-4 py-3 text-sm font-medium text-gray-900">
                {groupLabel(group)}
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-2 font-medium">Metric</th>
                      <th className="px-4 py-2 font-medium">Formula</th>
                      <th className="px-4 py-2 font-medium">Source</th>
                      <th className="px-4 py-2 font-medium">Owner</th>
                      <th className="px-4 py-2 font-medium">Defined on</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {metrics.map((metric) => (
                      <tr
                        key={metric.id}
                        data-testid={`definition-${metric.id}`}
                        className="align-top"
                      >
                        <td className="px-4 py-3">
                          <span className="font-medium text-gray-900">
                            {metric.label}
                          </span>
                          <span className="mt-0.5 block text-xs text-gray-500">
                            {metric.question}
                          </span>
                          {metric.executive !== undefined && (
                            <span className="mt-1 inline-block rounded bg-sky-50 px-1.5 py-0.5 text-[11px] font-medium text-sky-700">
                              Executive figure {metric.executive}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {metric.formula}
                          {metric.provisionalBelow !== undefined && (
                            <span className="mt-1 block text-xs text-gray-500">
                              Marked provisional below {metric.provisionalBelow}{" "}
                              subjects.
                            </span>
                          )}
                        </td>
                        {/* A metric with no writer says so here rather than
                            reading as an ordinary source nobody can find. */}
                        <td className="px-4 py-3 text-gray-700">
                          {metric.source === UNMEASURED_SOURCE ? (
                            <span className="font-medium text-amber-800">
                              Not yet measured
                            </span>
                          ) : (
                            metric.source
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {metric.owner}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                          {metric.definedOn}
                          <span className="mt-0.5 block text-xs text-gray-400">
                            {refreshLabel(metric.refreshSeconds)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          );
        })}

      {payload && (
        <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <h2 className="border-b border-gray-200 px-4 py-3 text-sm font-medium text-gray-900">
            Targets
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Target</th>
                  <th className="px-4 py-2 font-medium">Month 3</th>
                  <th className="px-4 py-2 font-medium">Month 6</th>
                  <th className="px-4 py-2 font-medium">Month 12</th>
                  <th className="px-4 py-2 font-medium">Set by</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {payload.targets.map((target) => (
                  <tr key={target.key}>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {target.label}
                      <span className="mt-0.5 block text-xs text-gray-500">
                        {target.source}
                      </span>
                    </td>
                    <td className="px-4 py-3 tabular-nums text-gray-700">
                      {target.month3}
                    </td>
                    <td className="px-4 py-3 tabular-nums text-gray-700">
                      {target.month6}
                    </td>
                    <td className="px-4 py-3 tabular-nums text-gray-700">
                      {target.month12}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {target.author}
                      <span className="mt-0.5 block text-xs text-gray-400">
                        {target.definedOn}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
